/**
 * <load-more-button>
 *
 * Replaces the legacy global loadMoreProducts() driven by collection-filter.js.
 * Fetches the next page via the Section Rendering API, appends the new product
 * cards to the current grid, and updates the URL via history.replaceState.
 *
 * Markup:
 *   <load-more-button
 *     data-next-url="/collections/foo?page=2"
 *     data-section-id="template--xxx__main-collection-product-grid"
 *     data-grid-selector="ul#product-grid">
 *     <button>Show more</button>
 *   </load-more-button>
 *
 * Listens for the `facets:render-finished` event (fired by facets.js after
 * a filter-triggered re-render) so the next-page state is re-read from the
 * freshly rendered DOM.
 */
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
    this.button?.addEventListener("click", (e) => {
      e.preventDefault();
      this.loadNextPage();
    });
    document.addEventListener("facets:render-finished", () => this.reset());
  }

  get nextUrl() {
    return this.dataset.nextUrl || "";
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get grid() {
    return document.querySelector(this.dataset.gridSelector || "ul#product-grid");
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.setAttribute("aria-busy", isLoading ? "true" : "false");
    this.button.disabled = isLoading;
  }

  async loadNextPage() {
    if (!this.nextUrl || !this.sectionId) {
      this.hide();
      return;
    }

    this.setLoading(true);

    try {
      const sep = this.nextUrl.includes("?") ? "&" : "?";
      const fetchUrl = `${this.nextUrl}${sep}section_id=${this.sectionId}`;
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, "text/html");
      const incomingGrid = doc.querySelector(this.dataset.gridSelector || "ul#product-grid");
      const incomingLoadMore = doc.querySelector("load-more-button");

      if (incomingGrid && this.grid) {
        const fragment = document.createDocumentFragment();
        Array.from(incomingGrid.children).forEach((node) => {
          fragment.appendChild(node);
        });
        this.grid.appendChild(fragment);
      }

      const nextUrl = incomingLoadMore?.dataset?.nextUrl;
      if (nextUrl) {
        this.dataset.nextUrl = nextUrl;
        try {
          history.replaceState(history.state, "", this.nextUrl);
        } catch (_) {
          /* noop: URL update is best-effort */
        }
      } else {
        this.hide();
      }

      this.dispatchEvent(
        new CustomEvent("load-more:appended", { bubbles: true })
      );
    } catch (err) {
      console.error("load-more-button: failed to load next page", err);
    } finally {
      this.setLoading(false);
    }
  }

  reset() {
    const freshButton = document.querySelector("load-more-button");
    if (!freshButton || freshButton === this) return;
    this.dataset.nextUrl = freshButton.dataset.nextUrl || "";
    if (!this.dataset.nextUrl) this.hide();
  }

  hide() {
    this.classList.add("hidden");
  }
}

customElements.define("load-more-button", LoadMoreButton);
